/**
 * Forfaits — Catalogue forfaits séance / hospitalisation / dialyse
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Coins, Search, Hospital, Stethoscope, Activity, Baby, Building2 } from "lucide-react";

interface Forfait {
  id: string;
  code: string;
  label: string;
  category: "dialyse" | "chimiotherapie" | "hospitalisation" | "consultation" | "maternite" | "urgence";
  description: string;
  priceMAD: number;
  cnssCoverage: number;
  cnopsCoverage: number;
  perSession: boolean;
  perDay: boolean;
}

const FORFAITS: Forfait[] = [
  { id: "f-001", code: "DIAL-HD", label: "Dialyse hémodialyse", category: "dialyse", description: "Séance d'hémodialyse chronique (3-4h)", priceMAD: 1200, cnssCoverage: 100, cnopsCoverage: 100, perSession: true, perDay: false },
  { id: "f-002", code: "DIAL-DP", label: "Dialyse péritonéale", category: "dialyse", description: "Dialyse péritonéale continue ambulatoire (DPCA)", priceMAD: 950, cnssCoverage: 100, cnopsCoverage: 100, perSession: true, perDay: false },
  { id: "f-003", code: "CHIMIO-J", label: "Chimiothérapie jour", category: "chimiotherapie", description: "Séance de chimiothérapie ambulatoire", priceMAD: 2500, cnssCoverage: 100, cnopsCoverage: 100, perSession: true, perDay: false },
  { id: "f-004", code: "HOSP-MED", label: "Hospitalisation médecine", category: "hospitalisation", description: "Forfait journalier hospitalisation service médecine", priceMAD: 350, cnssCoverage: 80, cnopsCoverage: 90, perSession: false, perDay: true },
  { id: "f-005", code: "HOSP-CHIR", label: "Hospitalisation chirurgie", category: "hospitalisation", description: "Forfait journalier hospitalisation service chirurgie", priceMAD: 500, cnssCoverage: 80, cnopsCoverage: 90, perSession: false, perDay: true },
  { id: "f-006", code: "HOSP-REA", label: "Réanimation", category: "hospitalisation", description: "Forfait journalier service réanimation", priceMAD: 1800, cnssCoverage: 100, cnopsCoverage: 100, perSession: false, perDay: true },
  { id: "f-007", code: "HOSP-PED", label: "Hospitalisation pédiatrique", category: "hospitalisation", description: "Forfait journalier service pédiatrie", priceMAD: 400, cnssCoverage: 100, cnopsCoverage: 100, perSession: false, perDay: true },
  { id: "f-008", code: "CONS-LONG", label: "Consultation longue", category: "consultation", description: "Consultation longue (≥30min)", priceMAD: 250, cnssCoverage: 70, cnopsCoverage: 80, perSession: true, perDay: false },
  { id: "f-009", code: "MAT-ACC", label: "Accouchement simple", category: "maternite", description: "Forfait accouchement voie basse sans complication", priceMAD: 3500, cnssCoverage: 100, cnopsCoverage: 100, perSession: true, perDay: false },
  { id: "f-010", code: "MAT-CES", label: "Césarienne", category: "maternite", description: "Forfait césarienne", priceMAD: 6500, cnssCoverage: 100, cnopsCoverage: 100, perSession: true, perDay: false },
  { id: "f-011", code: "URG-PASS", label: "Passage aux urgences", category: "urgence", description: "Forfait passage aux urgences (consultation + observation ≤6h)", priceMAD: 400, cnssCoverage: 80, cnopsCoverage: 80, perSession: true, perDay: false },
  { id: "f-012", code: "URG-OBS", label: "Observation urgences", category: "urgence", description: "Forfait observation prolongée urgences (>6h, ≤24h)", priceMAD: 700, cnssCoverage: 80, cnopsCoverage: 80, perSession: true, perDay: false },
];

const CATEGORY_LABEL: Record<Forfait["category"], string> = {
  dialyse: "Dialyse",
  chimiotherapie: "Chimiothérapie",
  hospitalisation: "Hospitalisation",
  consultation: "Consultation",
  maternite: "Maternité",
  urgence: "Urgences",
};

const CATEGORY_ICON: Record<Forfait["category"], any> = {
  dialyse: Activity,
  chimiotherapie: Stethoscope,
  hospitalisation: Hospital,
  consultation: Stethoscope,
  maternite: Baby,
  urgence: Building2,
};

export const Route = createFileRoute("/referential/forfaits")({
  component: ForfaitsReferential,
});

function ForfaitsReferential() {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<string>("all");

  const filtered = useMemo(() => FORFAITS.filter((f) => {
    if (filterCat !== "all" && f.category !== filterCat) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return f.code.toLowerCase().includes(s) || f.label.toLowerCase().includes(s) || f.description.toLowerCase().includes(s);
  }), [search, filterCat]);

  const categories = Array.from(new Set(FORFAITS.map((f) => f.category)));
  const stats = useMemo(() => {
    const byCat: Record<string, number> = {};
    FORFAITS.forEach((f) => byCat[f.category] = (byCat[f.category] || 0) + 1);
    return byCat;
  }, []);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Coins className="h-5 w-5" /> Forfaits
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Séance · Hospitalisation · Dialyse · Chimiothérapie · Maternité · Urgences
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {stats && Object.entries(stats).map(([cat, count]) => {
          const Icon = CATEGORY_ICON[cat as Forfait["category"]];
          return (
            <Card key={cat} className="cursor-pointer hover:border-primary/30" onClick={() => setFilterCat(cat)}>
              <CardContent className="p-3 text-center">
                <Icon className="h-5 w-5 mx-auto text-muted-foreground" />
                <div className="mt-1 text-xs font-medium">{CATEGORY_LABEL[cat as Forfait["category"]]}</div>
                <div className="text-lg font-bold">{count}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (code, libellé)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button size="sm" variant={filterCat === "all" ? "default" : "outline"} onClick={() => setFilterCat("all")}>
          Tous
        </Button>
        {categories.map((c) => (
          <Button
            key={c}
            size="sm"
            variant={filterCat === c ? "default" : "outline"}
            onClick={() => setFilterCat(c)}
          >
            {CATEGORY_LABEL[c as Forfait["category"]]}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {filtered.map((f) => {
          const Icon = CATEGORY_ICON[f.category];
          return (
            <Card key={f.id}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <code className="font-mono text-xs font-bold">{f.code}</code>
                      <span className="font-medium text-sm truncate">{f.label}</span>
                      <Badge variant="outline" className="text-[10px]">{CATEGORY_LABEL[f.category]}</Badge>
                      {f.perSession && <Badge variant="secondary" className="text-[9px]">/séance</Badge>}
                      {f.perDay && <Badge variant="secondary" className="text-[9px]">/jour</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{f.description}</div>
                  </div>
                  <div className="text-right text-xs shrink-0">
                    <div className="text-base font-bold">{f.priceMAD} MAD</div>
                    <div className="text-[10px] text-emerald-400">CNSS {f.cnssCoverage}% / CNOPS {f.cnopsCoverage}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
